import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { catalogueStats, datasets } from 'app/data/datasets';

/**
 * The hero.
 *
 * Every figure rendered here is computed from the dataset registry
 * (`app/data/datasets`) — no catalogue fact is written as prose, so the copy
 * cannot drift from the data.
 *
 * Register: monospace for numerics, labels and chips; sans for prose only.
 * No decorative imagery (the previous 60-line SVG chart was bound to no data)
 * and no entrance animations — the visitor arrives to evaluate whether a
 * dataset fits a strategy, so the first paint is the catalogue's numbers and a
 * way into it.
 *
 * Mobile: the text block is first in the DOM and there is nothing above it, so
 * at 390px the heading, the figures and the search affordance are what paints.
 */
export const SoftwareSignals = () => {
  const navigate = useNavigate();

  const stats = useMemo(() => catalogueStats(), []);

  /**
   * Survivorship handling, stated as a count rather than as a claim. Every
   * populated universe string in the registry names delisted companies
   * explicitly, so we count them instead of asserting it in copy.
   *
   * TODO(data): we do not publish a count of inactive/delisted entities. Once
   * it is measured, surface it here — it is the figure a buyer diligences.
   * Do not estimate it.
   */
  const universeCoverage = useMemo(() => {
    const declared = datasets.filter(d => d.universe);
    return {
      declared: declared.length,
      withDelisted: declared.filter(d => d.universe?.includes('delisted'))
        .length,
    };
  }, []);

  const earliestYear = stats.earliestHistory?.slice(0, 4);

  /** Which dataset each headline figure came from — derived, never typed out. */
  const earliestDataset = datasets.find(
    d => d.historyStart === stats.earliestHistory,
  );
  const widestDataset = datasets.find(
    d => d.entities === stats.deepestEntities,
  );

  const headlineStats: Array<{ value: string; label: string }> = [
    {
      value: String(stats.total),
      label: `datasets · ${stats.categories.equity} equity / ${stats.categories.economic} economic / ${stats.categories.sectorial} sectorial`,
    },
    {
      value: earliestYear ?? '—',
      label: earliestDataset
        ? `earliest observation · ${earliestDataset.name}`
        : 'earliest observation',
    },
    {
      value: stats.deepestEntities.toLocaleString('en-US'),
      label: widestDataset
        ? `entities, widest dataset · ${widestDataset.name}`
        : 'entities, widest dataset',
    },
    {
      value: `${stats.documented}/${stats.total}`,
      label: `with published history start & entity count · ${stats.beta} in beta`,
    },
  ];

  return (
    <div
      id="features"
      className="flex flex-col items-center bg-[#0d1117] border-b border-[#1d2838] pt-10 pb-12 px-4 sm:px-6 lg:px-8"
    >
      <div className="flex flex-col w-full max-w-5xl mx-auto gap-8">
        <div className="flex flex-col items-start text-left gap-y-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-gray-400">
            Dataset catalogue
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight">
            <span className="font-mono tabular-nums text-blue-400">
              {stats.total}
            </span>{' '}
            ticker-linked alternative datasets, queried from Python
          </h1>

          <p className="text-base md:text-lg text-gray-300 max-w-2xl">
            Equity, economic and sectorial data joined to listed and delisted US
            companies. History reaches back to {earliestYear}; the widest
            dataset covers {stats.deepestEntities.toLocaleString('en-US')}{' '}
            entities. History start, entity count and universe are published per
            dataset, and the fields we have not measured are shown as gaps
            rather than left out.
          </p>

          {/* Straight into the catalogue — the job the visitor arrived with. */}
          <Link
            to="/datasets"
            className="font-mono text-sm px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-semibold whitespace-nowrap no-underline"
          >
            Browse all {stats.total} →
          </Link>

          <div className="font-mono text-xs text-gray-400">
            <a
              href="https://docs.sov.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white underline underline-offset-2"
            >
              docs.sov.ai
            </a>
            <span className="mx-2 text-gray-700">|</span>
            <span>pip install sovai</span>
          </div>
        </div>

        {/* Figures. Replaces the decorative chart SVG, which was bound to nothing. */}
        <dl className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#1d2838] border border-[#1d2838] rounded-md overflow-hidden">
          {headlineStats.map(stat => (
            <div
              key={stat.label}
              className="flex flex-col gap-1.5 bg-[#0f1625] p-4"
            >
              <dt className="font-mono tabular-nums text-2xl sm:text-3xl font-bold text-white">
                {stat.value}
              </dt>
              <dd className="font-mono text-[11px] leading-snug text-gray-400">
                {stat.label}
              </dd>
            </div>
          ))}
        </dl>

        {/* Survivorship bias, as a count from the registry. */}
        <p className="text-sm text-gray-400 max-w-3xl">
          <span className="font-mono tabular-nums text-gray-200">
            {universeCoverage.withDelisted}
          </span>{' '}
          of the{' '}
          <span className="font-mono tabular-nums text-gray-200">
            {universeCoverage.declared}
          </span>{' '}
          datasets that declare an entity universe retain delisted companies, so
          a backtest over them is not restricted to today's survivors. The other{' '}
          <span className="font-mono tabular-nums text-gray-200">
            {stats.total - universeCoverage.declared}
          </span>{' '}
          are not company-level series, so they declare no universe.{' '}
          <button
            type="button"
            onClick={() => navigate('/datasets')}
            className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
          >
            Coverage per dataset
          </button>
        </p>
      </div>
    </div>
  );
};
